import { GridRowParams } from "@mui/x-data-grid-premium";

import { isManifestRow, ShippingPaperworkRow } from "./shippingPaperworkColumns";

/**
 * Where a `ManifestsV3` row click should go: the manifest drawer, keyed by the
 * `processedManifestId` URL path segment. A bill of lading is a manifest row
 * with a different kind, so it opens the same drawer.
 *
 * Reads the id off `row.manifest.id`, never the row's own `id`: an item's id is
 * `<source>:<entity id>`, which is not a valid manifest id.
 *
 * Answers null for a row with no manifest, where there is nowhere to navigate.
 */
export type ManifestsV3RowDestination = { kind: "manifest"; manifestId: string };

export function manifestsV3RowDestination(
  row: ShippingPaperworkRow
): ManifestsV3RowDestination | null {
  if (isManifestRow(row)) {
    return { kind: "manifest", manifestId: row.manifest.id };
  }
  return null;
}

interface ManifestsV3RowClickArgs {
  openManifest: (manifestId: string) => void;
}

/**
 * `ManifestsV3`'s `onRowClick` wiring, pulled out so a test can drive it with a
 * `GridRowParams`-shaped object and confirm the compound grid row id never
 * reaches `manifestsV3RowDestination` in place of the entity id. Reads
 * `params.row` only.
 */
export function manifestsV3RowClickHandler(
  params: Pick<GridRowParams<ShippingPaperworkRow>, "id" | "row">,
  { openManifest }: ManifestsV3RowClickArgs
): void {
  const destination = manifestsV3RowDestination(params.row);
  if (destination) {
    openManifest(destination.manifestId);
  }
}
